import { NgTemplateOutlet } from '@angular/common';
import { Component, ContentChild, Directive, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveAstralIconClass } from '../astral-icon-class.util';

export type AstralButtonIconPosition = 'before' | 'after';
export type AstralButtonSize = 'medium' | 'small' | 'extra-small';
export type AstralButtonVariant = 'primary' | 'secondary' | 'link-button' | 'cutout-button';

@Directive({
  selector: '[astralButtonIcon]',
  standalone: true,
})
export class AstralButtonIconDirective {}

@Component({
  selector: 'astral-button',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, TranslatePipe],
  template: `
    <ng-template #explicitIcon>
      <ng-content select="[astralButtonIcon]"></ng-content>
      @if (resolvedIconClass) { <i [class]="resolvedIconClass"></i> }
    </ng-template>

    <ng-template #buttonContent>
      @if ((hasProjectedIcon || resolvedIconClass) && iconPosition === 'before') {
        <span class="astral-button__icon" aria-hidden="true">
          <ng-container [ngTemplateOutlet]="explicitIcon"></ng-container>
        </span>
      }

      @if (showLeadingLinkVariantIcon) {
        <span class="astral-button__icon" aria-hidden="true">
          <i [class]="linkVariantIconClass"></i>
        </span>
      }

      @if (resolvedLabel) {
        <span class="astral-button__label">
          {{ resolvedLabel | translate }}
        </span>
      } @else {
        <span class="astral-button__label">
          <ng-content></ng-content>
        </span>
      }

      @if ((hasProjectedIcon || resolvedIconClass) && iconPosition === 'after') {
        <span class="astral-button__icon" aria-hidden="true">
          <ng-container [ngTemplateOutlet]="explicitIcon"></ng-container>
        </span>
      }

    </ng-template>

    @if (href) {
      <a
        class="astral-button"
        [class.astral-button--primary]="variant === 'primary'"
        [class.astral-button--secondary]="variant === 'secondary'"
        [class.astral-button--link-button]="variant === 'link-button'"
        [class.astral-button--cutout-button]="variant === 'cutout-button'"
        [class.astral-button--small]="size === 'small'"
        [class.astral-button--extra-small]="size === 'extra-small'"
        [class.astral-button--icon-only]="isIconOnly"
        [attr.href]="disabled ? null : href"
        [attr.target]="target"
        [attr.rel]="target === '_blank' ? 'noopener noreferrer' : null"
        [attr.tabindex]="disabled ? -1 : 0"
        [attr.aria-disabled]="disabled ? 'true' : null"
        [attr.aria-label]="ariaLabel ? (ariaLabel | translate) : null"
        [attr.aria-pressed]="ariaPressed"
        [attr.title]="title ? (title | translate) : null"
        (click)="handleDisabledLinkClick($event)"
      ><ng-container [ngTemplateOutlet]="buttonContent"></ng-container></a>
    } @else if (link) {
      <a
        class="astral-button"
        [class.astral-button--primary]="variant === 'primary'"
        [class.astral-button--secondary]="variant === 'secondary'"
        [class.astral-button--link-button]="variant === 'link-button'"
        [class.astral-button--cutout-button]="variant === 'cutout-button'"
        [class.astral-button--small]="size === 'small'"
        [class.astral-button--extra-small]="size === 'extra-small'"
        [class.astral-button--icon-only]="isIconOnly"
        [routerLink]="disabled ? null : link"
        [attr.tabindex]="disabled ? -1 : 0"
        [attr.aria-disabled]="disabled ? 'true' : null"
        [attr.aria-label]="ariaLabel ? (ariaLabel | translate) : null"
        [attr.aria-pressed]="ariaPressed"
        [attr.title]="title ? (title | translate) : null"
        (click)="handleDisabledLinkClick($event)"
      ><ng-container [ngTemplateOutlet]="buttonContent"></ng-container></a>
    } @else {
      <button
        class="astral-button"
        [class.astral-button--primary]="variant === 'primary'"
        [class.astral-button--secondary]="variant === 'secondary'"
        [class.astral-button--link-button]="variant === 'link-button'"
        [class.astral-button--cutout-button]="variant === 'cutout-button'"
        [class.astral-button--small]="size === 'small'"
        [class.astral-button--extra-small]="size === 'extra-small'"
        [class.astral-button--icon-only]="isIconOnly"
        [attr.type]="type"
        [disabled]="disabled"
        [attr.aria-label]="ariaLabel ? (ariaLabel | translate) : null"
        [attr.aria-pressed]="ariaPressed"
        [attr.title]="title ? (title | translate) : null"
      ><ng-container [ngTemplateOutlet]="buttonContent"></ng-container></button>
    }
  `,
  styleUrls: ['./astral-button.component.scss'],
})
export class AstralButtonComponent {
  @Input() ariaLabel: string;
  @Input() ariaPressed: boolean | null = null;
  @Input() disabled = false;
  /** External URL. Use `link` instead for an Angular router target. */
  @Input() href: string | null = null;
  @Input() icon: string;
  @Input() iconPosition: AstralButtonIconPosition = 'before';
  @Input() label: string;
  @Input() labelKey: string;
  /**
   * Router target. Setting it renders the button as a real anchor, so the browser's own
   * link behaviour - middle click, ctrl/cmd-click, "open in new tab", "copy link" and the
   * status bar target - works without any click handling of ours.
   *
   * Deliberately not named `routerLink`: Angular's own RouterLink directive matches any
   * element carrying that attribute, so `<astral-button [routerLink]="...">` would bind the
   * directive to the host element as well as this input. The host is not an anchor, so that
   * copy of the directive navigates on every click, modifier keys included - the current tab
   * would follow the link while the anchor opened a second one in a new tab.
   */
  @Input() link: string | any[] | null = null;
  @Input() size: AstralButtonSize = 'medium';
  @Input() target: '_self' | '_blank' = '_self';
  @Input() title: string;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() variant: AstralButtonVariant = 'primary';

  @ContentChild(AstralButtonIconDirective) buttonIcon: AstralButtonIconDirective;

  get hasProjectedIcon(): boolean {
    return !!this.buttonIcon;
  }

  get resolvedIconClass(): string | null {
    return resolveAstralIconClass(this.icon);
  }

  get linkVariantIconClass(): string | null {
    return resolveAstralIconClass('externalLink');
  }

  get showLeadingLinkVariantIcon(): boolean {
    return this.variant === 'link-button' && !this.hasProjectedIcon && !this.resolvedIconClass;
  }

  get resolvedLabel(): string {
    return this.labelKey ?? this.label ?? '';
  }

  get isIconOnly(): boolean {
    return !!this.resolvedIconClass && !this.resolvedLabel;
  }

  handleDisabledLinkClick(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
